import "./StatusPlot.css";
import React, { FunctionComponent } from "react";
import { InstrumentTableRunInfo } from "../../../../data/services/instrument-table-service";
import { CellType, StatusCell } from "../statuscell/StatusCell";

const txtPending = "Pending";
const txtLoading = "Loading";
const txtSequencing = "Sequencing";
const txtComplete = "Complete";

const STATUS_LABELS = [txtPending, txtLoading, txtSequencing, txtComplete];

interface StatusPlotProps {
    runInfos: InstrumentTableRunInfo[];
    style?: React.CSSProperties;
}

interface StatusPlotHeaderProps {
    style?: React.CSSProperties;
}

interface StatusPlotRowProps {
    runInfo: InstrumentTableRunInfo;
    style?: React.CSSProperties;
}

function buildCellTypes(runInfo: InstrumentTableRunInfo) {
    const counts = runInfo.cellProgressCounts ?? ({} as any);

    const safeCount = (value: unknown): number => {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? n : 0;
    };

    const pending = safeCount(counts.pending);
    const loading = safeCount(counts.loading);
    const sequencing = safeCount(counts.sequencing);
    const complete = safeCount(counts.complete);

    const repeat = (type: CellType, n: number): CellType[] =>
        Array.from({ length: n }, () => type);

    return {
        pending: repeat("ok", pending),
        loading: repeat("ok", loading),
        sequencing: repeat("ok", sequencing),
        complete: repeat("ok", complete),
    };
}

function hasAnyCells(runInfo: InstrumentTableRunInfo): boolean {
    const counts = runInfo.cellProgressCounts ?? ({} as any);
    const pending = Number(counts.pending ?? 0);
    const loading = Number(counts.loading ?? 0);
    const sequencing = Number(counts.sequencing ?? 0);
    const complete = Number(counts.complete ?? 0);
    return pending + loading + sequencing + complete > 0;
}

/**
 * Header grid (4 columns, full width)
 */
export const StatusPlotHeader: FunctionComponent<StatusPlotHeaderProps> = ({ style }) => (
    <div className="statusplot-grid statusplot-header" style={style}>
        {STATUS_LABELS.map((label) => (
            <div key={label} className="statusplot-header-cell">
                {label}
            </div>
        ))}
    </div>
);

/**
 * Single row – its own 4-column grid, full width.
 * Can be used inside StatusPlot or standalone.
 */
export const StatusPlotRow: FunctionComponent<StatusPlotRowProps> = ({ runInfo, style }) => {
    const { pending, loading, sequencing, complete } = buildCellTypes(runInfo);
    const groups: CellType[][] = [pending, loading, sequencing, complete];

    return (
        <div className="statusplot-grid statusplot-row" style={style}>
            {groups.map((types, index) => (
                <div key={index} className="statusplot-cell">
                    <StatusCell cellTypes={types} />
                </div>
            ))}
        </div>
    );
};

/**
 * Main plot component – header + stacked rows.
 */
export const StatusPlot: FunctionComponent<StatusPlotProps> = ({ runInfos, style }) => {
    const nonEmptyRunInfos = runInfos.filter(hasAnyCells);
    if (nonEmptyRunInfos.length === 0) return null;

    return (
        <div className="statusplot-container" style={style}>
            <StatusPlotHeader />
            <div className="statusplot-body">
                {nonEmptyRunInfos.map((ri) => (
                    <StatusPlotRow key={ri.runId} runInfo={ri} />
                ))}
            </div>
        </div>
    );
};
